import { createContext, CSSProperties, useContext } from 'react';
import { AvatarGroupProps } from '@mui/material';

import { CellAlign } from '../cells.type';
import { AvatarProps } from './Avatar';

/**
 * In many cases, user has below structure:
 * {
 *    email?: string;
 *    firstname?: string;
 *    lastname?: string;
 *    thumbnail?: string;
 * }
 *
 * So let's generate avatar content below priority
 * 1. if (thumbnail) { render image with thumbnail }
 * 2. if (firstname || lastname) { render 2 characters content with first and last names }
 * 3. if (email) { render no_image icon }
 * 4. { render no_image icon }
 */

type AvatarCellContextType = {
  align: CellAlign;
  data: AvatarProps[];
  tooltip?: any;
  avatarGroupProps: AvatarGroupProps;
  avatarStyle: CSSProperties;
};

const defaultAvatarCellContextValue: AvatarCellContextType = {
  align: 'left',
  data: [],
  avatarGroupProps: {},
  avatarStyle: {},
};

export const AvatarCellContext = createContext<AvatarCellContextType>(
  defaultAvatarCellContextValue
);

interface AvatarCellProviderProps {
  align?: CellAlign;
  data?: AvatarProps[];
  tooltip?: any;
  avatarGroupProps?: AvatarGroupProps;
  avatarStyle?: CSSProperties;
  children?: React.ReactNode;
}

export const AvatarCellProvider = ({
  align = 'left',
  data = [],
  tooltip,
  avatarGroupProps = {
    sx: {
      justifyContent: 'flex-end',
    },
  },
  avatarStyle = {
    width: 28,
    height: 28,
    fontSize: 14,
    marginLeft: -2,
    cursor: 'pointer',
  },
  children,
}: AvatarCellProviderProps) => {
  return (
    <AvatarCellContext.Provider
      value={{ align, data, tooltip, avatarGroupProps, avatarStyle }}
    >
      {children}
    </AvatarCellContext.Provider>
  );
};

export const useAvaratCellContext = () => {
  return useContext(AvatarCellContext);
};
